import { useCallback, useEffect, useRef, useState } from 'react'
import { BACKEND_URL } from '@/lib/config'
import type { ConnectorPrompt } from '@/lib/connector-prompts'
import type { Platform } from '@/lib/platform'
import { getAuthToken, useSupabaseAuth } from '@/lib/supabase'

/**
 * Read the active sheet's headers and a few sample rows.
 * Uses Office.js directly (Excel) or Apps Script (Sheets).
 * Returns null if the sheet is empty or reading fails.
 */
async function readSheetSample(
  platform: Platform
): Promise<{ headers: string[]; sampleRows: unknown[][] } | null> {
  try {
    if (platform === 'excel') {
      return await readExcelSample()
    }
    return await readSheetsSample()
  } catch {
    return null
  }
}

async function readExcelSample(): Promise<{
  headers: string[]
  sampleRows: unknown[][]
} | null> {
  const Excel = window.Excel
  if (!Excel) return null

  return Excel.run(async (context: Excel.RequestContext) => {
    const sheet = context.workbook.worksheets.getActiveWorksheet()
    const usedRange = sheet.getUsedRangeOrNullObject()
    usedRange.load(['values', 'rowCount', 'columnCount', 'isNullObject'])
    await context.sync()

    if (usedRange.isNullObject || usedRange.rowCount === 0) return null

    const values = usedRange.values as unknown[][]
    if (!values || values.length === 0) return null

    const headers = values[0]
      .map((v) => (v != null ? String(v) : ''))
      .filter((h) => h.length > 0)

    if (headers.length === 0) return null

    // Take up to 5 sample data rows (skip header)
    const sampleRows = values.slice(1, 6).map((row) =>
      row.slice(0, headers.length).map((v) => {
        if (v == null || v === '') return null
        return v
      })
    )

    return { headers, sampleRows }
  })
}

async function readSheetsSample(): Promise<{
  headers: string[]
  sampleRows: unknown[][]
} | null> {
  // For Google Sheets, use postMessage to Apps Script
  // callAppsScript is not easily importable here without circular deps,
  // so we use a lightweight inline version
  return new Promise((resolve) => {
    const isInIframe = window.self !== window.top
    if (!isInIframe) {
      resolve(null)
      return
    }

    const messageId = `sheet-sample-${Date.now()}`
    const timeout = setTimeout(() => {
      window.removeEventListener('message', handler)
      resolve(null)
    }, 3000)

    const handler = (event: MessageEvent) => {
      if (
        event.data?.type === 'APPS_SCRIPT_RESULT' &&
        event.data?.id === messageId
      ) {
        window.removeEventListener('message', handler)
        clearTimeout(timeout)
        try {
          const result = event.data.result
          if (result?.values?.length > 0) {
            const values = result.values as unknown[][]
            const headers = values[0]
              .map((v: unknown) => (v != null ? String(v) : ''))
              .filter((h: string) => h.length > 0)
            if (headers.length > 0) {
              const sampleRows = values.slice(1, 6).map((row: unknown[]) =>
                row.slice(0, headers.length).map((v: unknown) => {
                  if (v == null || v === '') return null
                  return v
                })
              )
              resolve({ headers, sampleRows })
              return
            }
          }
          resolve(null)
        } catch {
          resolve(null)
        }
      }
    }

    window.addEventListener('message', handler)
    window.parent.postMessage(
      {
        type: 'APPS_SCRIPT_CALL',
        id: messageId,
        functionName: 'readRange',
        args: ['A1:Z6'],
      },
      '*'
    )
  })
}

/**
 * Call the backend suggestions endpoint.
 */
async function fetchSheetSuggestions(
  headers: string[],
  sampleRows: unknown[][],
  connectorTypes: string[]
): Promise<ConnectorPrompt[]> {
  const token = await getAuthToken()
  if (!token || !BACKEND_URL) return []

  const response = await fetch(`${BACKEND_URL}/api/suggestions/sheet-prompts`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'ngrok-skip-browser-warning': 'true',
    },
    body: JSON.stringify({
      headers,
      sample_rows: sampleRows,
      connector_types: connectorTypes,
    }),
  })

  if (!response.ok) return []

  const data = await response.json()
  return (data.suggestions ?? []) as ConnectorPrompt[]
}

export type SheetSuggestionsResult = {
  suggestions: ConnectorPrompt[]
  isLoading: boolean
}

/**
 * Hook that reads the active sheet and generates AI-powered prompt suggestions.
 *
 * - Reads sheet data silently via Office.js / Apps Script (no tool calls shown)
 * - Calls a fast/cheap model on the backend to generate contextual prompts
 * - Returns suggestions only when the sheet has data
 * - Caches results per sheet content hash to avoid redundant calls
 */
export function useSheetSuggestions(
  platform: Platform,
  connectorTypes: string[],
  enabled: boolean
): SheetSuggestionsResult {
  const { isAuthenticated } = useSupabaseAuth()
  const [suggestions, setSuggestions] = useState<ConnectorPrompt[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const lastHashRef = useRef<string>('')
  const abortRef = useRef<AbortController | null>(null)

  const generate = useCallback(async () => {
    if (!enabled || !isAuthenticated) return

    // Read sheet sample
    const sample = await readSheetSample(platform)
    if (!sample) return

    // Simple content hash to avoid re-fetching for the same data
    const hash = JSON.stringify(sample.headers)
    if (hash === lastHashRef.current) return
    lastHashRef.current = hash

    // Abort previous request if still in flight
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    setIsLoading(true)
    try {
      const result = await fetchSheetSuggestions(
        sample.headers,
        sample.sampleRows,
        connectorTypes
      )
      if (!controller.signal.aborted && result.length > 0) {
        setSuggestions(result)
      }
    } catch {
      // Silent failure — static suggestions remain
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false)
      }
    }
  }, [platform, connectorTypes, enabled, isAuthenticated])

  useEffect(() => {
    generate()
  }, [generate])

  return { suggestions, isLoading }
}
